import type { RouteStop } from '../../../shared/types';
import { googlePlaceId } from '../data/corridor';
import type { PlaceMark } from '../data/daily';
import { clockText } from '../logic/daily';
import { btn } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import { PLACE_MARK_LABELS } from '../ui/labels';

/** One stop of a planned day on Plan tomorrow (D83). A place from the area's pool has one Skip, whose
 *  reasons open in the card (D85): just this day, or a mark that takes it out of the pool for good. */

const MARKS: PlaceMark[] = ['not_fit', 'chain', 'square'];
const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

export interface StopCardActions {
  /** Plan again without this stop, this day only. */
  onRemove: (stop: RouteStop) => void;
  onMark: (stop: RouteStop, mark: PlaceMark) => void;
}

/** The card's actions: Call first, then Remove — or, for a place from the pool, Skip and its reasons. */
const actionRow = (stop: RouteStop, actions: StopCardActions | null): HTMLElement => {
  // a follow-up is a lead (marked from its page), and a meeting stays on the day
  const pool = !!actions && !stop.meeting && !stop.lead_id && (stop.place_id.startsWith('roster:') || googlePlaceId(stop) !== null);
  const row = h('div', { class: 'stopcard__actions' });
  const callFirst =
    stop.call_first && stop.phone
      ? h('a', { class: 'btn btn--subtle btn--sm', href: telHref(stop.phone) }, `Call first · ${stop.phone}`)
      : null;
  const closed = (): void => {
    if (!actions || stop.meeting) return void replaceChildren(row, callFirst);
    const remove = () => actions.onRemove(stop);
    if (!pool)
      return void replaceChildren(
        row,
        callFirst,
        btn('Remove', { variant: 'subtle', size: 'sm', onClick: remove }),
      );
    replaceChildren(row, callFirst, btn('Skip', { variant: 'subtle', size: 'sm', onClick: open }));
  };
  const open = (): void => {
    if (!actions) return;
    replaceChildren(
      row,
      h(
        'div',
        { class: 'stopcard__skip' },
        btn('Just this day', { variant: 'subtle', size: 'sm', onClick: () => actions.onRemove(stop) }),
        MARKS.map((mark) =>
          btn(PLACE_MARK_LABELS[mark], {
            variant: 'ghost',
            size: 'sm',
            onClick: () => actions.onMark(stop, mark),
          }),
        ),
        btn('Cancel', { variant: 'ghost', size: 'sm', onClick: closed }),
      ),
    );
  };
  closed();
  return row;
};

export const planStopCard = (
  stop: RouteStop & { late?: boolean },
  i: number,
  actions: StopCardActions | null,
): HTMLElement =>
  h(
    'article',
    { class: ['stopcard', stop.meeting ? 'stopcard--meeting' : ''].filter(Boolean).join(' ') },
    h('div', { class: 'stopcard__num mono' }, String(i + 1)),
    h(
      'div',
      { class: 'stopcard__body' },
      stop.arrive ? h('div', { class: 'stopcard__arrive mono' }, clockText(stop.arrive)) : null,
      h('div', { class: 'stopcard__name' }, stop.name),
      stop.note ? h('div', { class: 'stopcard__note' }, stop.note) : null,
      stop.late
        ? h('div', { class: 'stopcard__note stopcard__late' }, 'The day cannot reach it on time')
        : null,
      stop.address ? h('div', { class: 'stopcard__addr' }, stop.address) : null,
      actionRow(stop, actions),
    ),
  );
